import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import { createPaymentDetailsReportDownload, getPaymentDetailsReport } from '../../../../APICalling/APICalling';
import Loader from '../../../../Components/Loader/Loader';
import Placeholder from '../../../../Components/Placeholder/Placeholder';
import SearchNotFoundIcon from '../../../../Components/Icons/SearchNotFoundIcon';
import ReportHeader from '../ReportHeader/ReportHeader';
import { calculateReportDateRange, getPeriodText, isReportRangeWithinMonths } from '../../../../Utils/ReportDateUtils';
import { attachSharedUserChannelListener } from '../../../../Utils/clearSharedUserChannelEvent';
import { downloadFileWithAuth, getDownloadFilename } from '../../../../Utils/downloadFileWithAuth';
import styles from './PaymentDetailsReport.module.css';

const getSaleLinkOutletId = (outletId) => {
    const parsed = Number(outletId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const PaymentDetailsReport = ({ filterParams }) => {
    const navigate = useNavigate();
    const { userAssignedOutlets } = useGlobalContext();

    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [outletName, setOutletName] = useState('');
    const [periodText, setPeriodText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [pagination, setPagination] = useState(null);

    const observer = useRef();
    const fetchPaymentDetailsRef = useRef();

    const getEligibleOutlets = useCallback(() => {
        const editableOutlets = (userAssignedOutlets || []).filter((o) => o.mode === 'edit');
        return editableOutlets.length > 0 ? editableOutlets : userAssignedOutlets || [];
    }, [userAssignedOutlets]);

    const getOutletIds = useCallback(
        (value) => {
            if (Array.isArray(value)) return value.map((id) => String(id).trim()).filter(Boolean);
            if (value === undefined || value === null) {
                return getEligibleOutlets().map((outlet) => String(outlet.id));
            }
            const raw = String(value).trim();
            if (!raw) {
                return [];
            }
            return raw.split(',').map((id) => id.trim()).filter(Boolean);
        },
        [getEligibleOutlets]
    );

    useEffect(() => {
        if (!userAssignedOutlets?.length) return;
        const outletIds = getOutletIds(filterParams?.outlet);
        if (!outletIds.length) {
            setOutletName('Unknown');
            return;
        }

        const eligibleOutlets = getEligibleOutlets();
        const names = outletIds
            .map((id) => eligibleOutlets.find((o) => String(o.id) === String(id))?.name)
            .filter(Boolean);
        setOutletName(names.length ? names.join(', ') : 'Unknown');
    }, [filterParams?.outlet, getEligibleOutlets, getOutletIds, userAssignedOutlets]);

    const getPageNumbers = (paginationInfo) => {
        const current = Number(
            paginationInfo?.currentPage ??
                paginationInfo?.current_page ??
                paginationInfo?.page ??
                paginationInfo?.current
        );
        const last = Number(
            paginationInfo?.lastPage ??
                paginationInfo?.last_page ??
                paginationInfo?.total_pages ??
                paginationInfo?.last
        );
        return {
            current: Number.isFinite(current) ? current : null,
            last: Number.isFinite(last) ? last : null,
        };
    };

    const fetchPaymentDetails = useCallback(
        async (page = 1, isInitialLoad = false) => {
            if (!filterParams || !filterParams.date) {
                setRows([]);
                setHasMore(false);
                setPagination(null);
                return;
            }

            const hasOutletFilter = Object.prototype.hasOwnProperty.call(filterParams, 'outlet');
            const outletIds = getOutletIds(filterParams?.outlet);

            try {
                if (isInitialLoad) {
                    setLoading(true);
                } else {
                    setLoadingMore(true);
                }

                const { startDate, endDate } = calculateReportDateRange(filterParams);

                const params = {
                    start_date: startDate,
                    end_date: endDate,
                    pagination: 1,
                    page,
                    archive: 1
                };
                if (hasOutletFilter) {
                    params.outlet_ids = outletIds.join(',');
                }

                // Staff filter (multi-select from Topbar)
                // Handle empty array (when all staff are unselected)
                if (filterParams.staff !== undefined) {
                    if (Array.isArray(filterParams.staff)) {
                        params.therapist = filterParams.staff.join(',');
                    } else {
                        params.therapist = String(filterParams.staff || '').trim();
                    }
                }
                if (filterParams.archive === 1) {
                    params.archive = 1;
                }

                if (isInitialLoad) {
                    setPeriodText(getPeriodText(filterParams));
                }

                const response = await getPaymentDetailsReport(params);
                const isSuccess = response?.data?.success ?? response?.success ?? false;

                if (!isSuccess) {
                    if (isInitialLoad) {
                        setRows([]);
                        toast.error(
                            response?.data?.message ||
                                response?.message ||
                                'Failed to fetch payment details'
                        );
                    }
                    setHasMore(false);
                    return;
                }

                const payload = response?.data?.data ?? response?.data?.data?.data ?? {};
                const dataList = Array.isArray(payload?.list) ? payload.list : [];
                const paginationInfo = payload?.pagination || null;

                if (isInitialLoad) {
                    setRows(dataList);
                } else {
                    setRows((prev) => [...prev, ...dataList]);
                }

                setPagination(paginationInfo);

                const { current, last } = getPageNumbers(paginationInfo);
                if (current && last) {
                    setHasMore(current < last);
                } else {
                    setHasMore(false);
                }
            } catch (error) {
                console.error('Error fetching payment details:', error);
                if (isInitialLoad) {
                    setRows([]);
                    toast.error('An error occurred while fetching payment details');
                }
                setHasMore(false);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [filterParams, getOutletIds]
    );

    useEffect(() => {
        fetchPaymentDetailsRef.current = fetchPaymentDetails;
    }, [fetchPaymentDetails]);

    useEffect(() => {
        if (filterParams && filterParams.date) {
            setCurrentPage(1);
            setRows([]);
            setHasMore(true);
            setPagination(null);
            fetchPaymentDetails(1, true);
        }
    }, [filterParams, fetchPaymentDetails]);

    const lastItemRef = useCallback(
        (node) => {
            if (loading || loadingMore) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMore && pagination) {
                    const nextPage = currentPage + 1;
                    const { last } = getPageNumbers(pagination);
                    if (last && nextPage <= last && fetchPaymentDetailsRef.current) {
                        setCurrentPage(nextPage);
                        fetchPaymentDetailsRef.current(nextPage, false);
                    }
                }
            });
            if (node) observer.current.observe(node);
        },
        [loading, loadingMore, hasMore, pagination, currentPage]
    );

    const formatCurrency = (value) => {
        const num = parseFloat(value || 0);
        if (Number.isNaN(num)) return '$ 0.00';
        return `$ ${num.toFixed(2)}`;
    };

    const formatSaleId = (saleNumber) => {
        const raw = String(saleNumber ?? '').trim();
        if (!raw || raw === '#') return '-';
        return raw;
    };

    const displayOrDash = (value) => {
        const raw = String(value ?? '').trim();
        if (!raw || raw.toLowerCase() === 'n/a' || raw.toLowerCase() === 'na') return '-';
        return raw;
    };

    const handleSaleIdClick = (e, saleNumber, outletId) => {
        e.stopPropagation();
        const raw = String(saleNumber ?? '').trim();
        if (!raw || raw === '#' || raw === '-') return;

        const saleId = raw.startsWith('#') ? raw.slice(1) : raw;
        const outletForLink = getSaleLinkOutletId(outletId);
        const url = `/sales?search=${saleId}&outlet=${outletForLink}`;
        window.open(url, '_blank');
    };

    const handleDownloadAll = async () => {
        if (!rows || rows.length === 0) {
            toast.warning('No data available to download');
            return;
        }

        if (!isReportRangeWithinMonths(filterParams, 3)) {
            toast.error('Please select a date range within 90 days to proceed.');
            return;
        }

        const userId = localStorage.getItem('user_id');
        const token = localStorage.getItem('token');

        if (!userId) {
            toast.error('User ID not found');
            return;
        }

        if (!token) {
            toast.error('Authentication token not found');
            return;
        }

        const channelName = `user.${userId}`;
        const eventName = 'PaymentDetailsReportReady';
        let channel;
        let detach = null;
        let toastId = null;

        setDownloadLoading(true);
        toastId = toast.loading('Downloading in progress...');

        try {
            // Join shared user channel — do not Echo.leave (payroll also uses user.{id}).

            const cleanup = () => {
                try {
                    detach?.();
                } catch (e) {}
                if (toastId) {
                    toast.dismiss(toastId);
                    toastId = null;
                }
            };

            const { channel: joinedChannel, detach: detachListener } = attachSharedUserChannelListener(
                channelName,
                eventName,
                async (event) => {
                    try {
                        const downloadUrl = event?.url || event?.data?.url;

                        if (!downloadUrl) {
                            toast.error('Download URL not received. Please try again.');
                            cleanup();
                            setDownloadLoading(false);
                            return;
                        }

                        try {
                            await downloadFileWithAuth(
                                downloadUrl,
                                getDownloadFilename(event, downloadUrl, 'payment-details.csv')
                            );
                            toast.success('Downloaded successfully');
                        } catch (downloadError) {
                            toast.error('Failed to start download');
                        }

                        cleanup();
                        setDownloadLoading(false);
                    } catch (error) {
                        toast.error('Failed to process download');
                        cleanup();
                        setDownloadLoading(false);
                    }
                }
            );
            channel = joinedChannel;
            detach = detachListener;

            if (typeof channel.error === 'function') {
                channel.error(() => {
                    toast.error('Socket connection error');
                    cleanup();
                    setDownloadLoading(false);
                });
            }

            // Wait for subscription to be ready before calling API
            await new Promise((resolve) => setTimeout(resolve, 500));

            const { startDate, endDate } = calculateReportDateRange(filterParams);
            const hasOutletFilter = Object.prototype.hasOwnProperty.call(filterParams || {}, 'outlet');
            const outletIds = getOutletIds(filterParams?.outlet);

            const downloadParams = {
                start_date: startDate,
                end_date: endDate,
                user_id: userId,
            };
            if (hasOutletFilter) {
                downloadParams.outlet_ids = outletIds.join(',');
            }

            if (filterParams?.staff !== undefined) {
                if (Array.isArray(filterParams.staff)) {
                    downloadParams.therapist = filterParams.staff.join(',');
                } else {
                    downloadParams.therapist = String(filterParams.staff || '').trim();
                }
            }
            if (filterParams?.archive === 1) {
                downloadParams.archive = 1;
            }

            const createResponse = await createPaymentDetailsReportDownload(downloadParams);

            const responseMessage =
                createResponse?.data?.data?.message ||
                createResponse?.data?.message ||
                createResponse?.message ||
                '';

            const status =
                createResponse?.data?.status ||
                createResponse?.status ||
                createResponse?.data?.data?.status;

            const successFlag =
                createResponse?.data?.data?.success ||
                createResponse?.data?.success ||
                createResponse?.success ||
                false;

            const isOk =
                Boolean(successFlag) ||
                status === 'processing' ||
                status === 'queued' ||
                status === 'success';

            if (isOk) {
                return; // wait for socket event
            }

            const backendError = responseMessage || 'Failed to initiate download';
            toast.error(backendError);
            cleanup();
            setDownloadLoading(false);
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                'An error occurred while preparing download';

            toast.error(errorMessage, { autoClose: 5000 });

            if (toastId) toast.dismiss(toastId);
            setDownloadLoading(false);

            try {
                detach?.();
            } catch (e) {}
        }
    };

    return (
        <div className={styles.paymentDetailsContainer}>
            <ReportHeader
                outletName={outletName}
                periodText={periodText}
                onDownload={handleDownloadAll}
                downloadLoading={downloadLoading}
                hasData={rows && rows.length > 0}
                downloadButtonText="Download CSV"
                hideDownload={true}
            />

            {loading ? (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            ) : rows && rows.length > 0 ? (
                <div className={styles.tableWrapper}>
                    <table className={styles.paymentTable}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            <tr>
                                <th className={styles.colSaleNumber}>Sale ID</th>
                                <th className={styles.colPaymentDate}>Payment Date</th>
                                <th className={styles.colSaleDate}>Sale Date</th>
                                <th className={styles.colClient}>Client</th>
                                <th className={styles.colStaff}>Staff Members</th>
                                <th className={styles.colSubtotal}>Sale Total</th>
                                <th className={styles.colAmount}>Amount</th>
                                <th className={styles.colMethod}>Payment Method</th>
                                <th className={styles.colReference}>Payment Ref #</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((item, index) => {
                                const isLastItem = index === rows.length - 1;
                                return (
                                    <tr
                                        key={`${item?.payment_transaction_id || 'payment'}-${index}`}
                                        ref={isLastItem ? lastItemRef : null}
                                    >
                                        <td className={styles.colSaleNumber}>
                                            {formatSaleId(item?.sale_id) !== '-' ? (
                                                <span
                                                    className={styles.saleIdLink}
                                                    onClick={(e) => handleSaleIdClick(e, item?.sale_id, item?.outlet_id)}
                                                >
                                                    {formatSaleId(item?.sale_id)}
                                                </span>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                        <td className={styles.colPaymentDate}>{displayOrDash(item?.payment_date)}</td>
                                        <td className={styles.colSaleDate}>{displayOrDash(item?.sale_date)}</td>
                                        <td className={styles.colClient}>{displayOrDash(item?.client)}</td>
                                        <td className={styles.colStaff}>{displayOrDash(item?.staff_members)}</td>
                                        <td className={styles.colSubtotal}>{formatCurrency(item?.sale_total)}</td>
                                        <td className={styles.colAmount}>{formatCurrency(item?.amount)}</td>
                                        <td className={styles.colMethod}>{displayOrDash(item?.payment_method)}</td>
                                        <td className={styles.colReference}>
                                            {displayOrDash(item?.original_reference ?? item?.payment_reference)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className={styles.noData}>
                    <Placeholder
                        icon={<SearchNotFoundIcon width="24" height="24" color="white" />}
                        heading="No payment details found"
                        para="Please try changing the filters"
                    />
                </div>
            )}
        </div>
    );
};

export default PaymentDetailsReport;

